import React from 'react';
import styled, { css } from 'styled-components';
import { DATA_GRABS, OrderConfirmationTextRole } from '../design';
import { useDesign } from '../DesignContext';

const ThemedTextBase = styled.span<{ $color: string }>`
    /* A site theme styles h1-h6 and paragraphs through two-ID selectors
     * (#dmRoot #dm div.dmContent h3) that no class selector here can
     * out-specify. Font color is its own design option now, so the widget owns
     * it either way — the picked value and the default alike have to be forced,
     * or the theme would repaint the text and the color picker would be lying
     * about what renders. Typography still comes from the picked text style. */
    ${({ $color }) => css`
        color: ${$color} !important;
    `}
`;

// `display: contents` keeps the host out of the layout while still giving the
// inner <p> the `div.dmNewParagraph` ancestor the site theme's paragraph rule
// (#dm div.dmNewParagraph p) requires.
const ParagraphHost = styled.div.attrs({ className: 'dmNewParagraph' })`
    display: contents;
`;

interface ThemedTextProps
    extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
    textRole: OrderConfirmationTextRole;
    /** Element rendered when the picked text style is the theme default. */
    defaultTag?: 'div' | 'span';
    /**
     * Keeps this text in the document outline when the picked text style is not
     * a heading tag: DFLT and PAR render a div/p, so a title would otherwise
     * drop out of the heading structure entirely.
     */
    headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
}

const HEADING_TAG = /^h[1-6]$/;

/**
 * Renders its text in the element the site theme styles for the text style
 * picked for `textRole`, carrying that role's `data-grab` so the editor's design
 * panel can target it. Wrap it with `styled()` to add the widget's own layout —
 * never typography, which belongs to the site theme alone.
 */
const ThemedText: React.FC<ThemedTextProps> = ({
    textRole,
    defaultTag = 'div',
    headingLevel,
    children,
    ...rest
}) => {
    const { textStyles, colors } = useDesign();
    const textStyle = textStyles[textRole];

    // A native h1-h6 already carries its own level, so the ARIA fallback is
    // only for the non-heading text styles.
    const headingAttrs =
        headingLevel && !HEADING_TAG.test(textStyle)
            ? { role: 'heading', 'aria-level': headingLevel }
            : {};

    const element = (
        <ThemedTextBase
            as={textStyle === 'default' ? defaultTag : textStyle}
            $color={colors[textRole]}
            data-grab={DATA_GRABS[textRole]}
            {...headingAttrs}
            {...rest}
        >
            {children}
        </ThemedTextBase>
    );

    return textStyle === 'p' ? (
        <ParagraphHost>{element}</ParagraphHost>
    ) : (
        element
    );
};

export default ThemedText;
